import { useMutation } from '@apollo/client';
import APPLY_UTM_TAGS from '~/jobiqo/components/SourceTracking/graphql/mutations/applyUtmTags.gql';
import { useConfig } from '~/jobiqo/context/config';

/**
 * Hook that provides a function to apply UTM tags to the user's profile.
 * Should be called after successful login or registration.
 */
export function useApplyUtmTags() {
  const [applyUtmTagsMutation] = useMutation(APPLY_UTM_TAGS);
  const { getConfigByName } = useConfig();
  const moduleEnabled: boolean = !!getConfigByName('jobiqo_utm_tagging')
    ?.value[0];
  const featureEnabled: boolean = !!getConfigByName('utm_tagging_enabled')
    ?.value[0];

  const applyUtmTags = async (
    query: Record<string, any> = {},
    jwt?: string,
  ) => {
    if (!moduleEnabled || !featureEnabled) {
      return;
    }

    const getQueryValue = (value: string | string[] | undefined) =>
      Array.isArray(value) ? value.join(',') : value ?? null;
    const utmCampaign = getQueryValue(query?.utm_campaign);
    const utmSource = getQueryValue(query?.utm_source);

    if (!utmCampaign && !utmSource) {
      return;
    }

    if (!jwt) {
      return;
    }

    const isProduction = process.env.NODE_ENV === 'production';
    const jwtAuthHeader = isProduction ? 'Authorization' : 'JWT-Authorization';

    try {
      const { data, errors } = await applyUtmTagsMutation({
        variables: { data: { utmCampaign, utmSource } },
        context: {
          headers: {
            [jwtAuthHeader]: `Bearer ${jwt}`,
          },
        },
      });
      const violations = data?.applyUtmTags?.errors;
      if (errors || violations?.length) {
        // UTM tagging is non-critical, so we only log failures.
        console.error('Failed to apply UTM tags.', errors ?? violations);
      }
    } catch (error) {
      console.error('Failed to apply UTM tags.', error);
    }
  };

  return { applyUtmTags };
}
